import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import { toMerged, omit } from 'es-toolkit';
import { DateRangeKey } from '@newo/ui-kit';
import { PAGE_SIZES } from '@/pages/Sessions/Sessions.utils.ts';
import { TPagination, TSessionFilters } from '@/pages/Sessions/Sessions.types';

interface ISessionsState {
    version: number;
    setVersion: (version: number) => void;
    hiddenColumns: string[];
    setHiddenColumns: (columns: string[]) => void;
    dateRange?: DateRangeKey;
    setDateRange: (dateRange?: DateRangeKey) => void;
    filters: TSessionFilters;
    setFilters: (filters: Partial<TSessionFilters>) => void;
    replaceFilters: (filters: Partial<TSessionFilters>) => void;
    resetFilters: () => void;
    pagination: TPagination;
    setPagination: (pagination: Partial<TPagination>) => void;
}

const initialState = {
    version: 0,
    hiddenColumns: [],
    dateRange: DateRangeKey.LAST_30_DAYS,
    filters: {
        channels: ['text', 'voice'],
        session_lengths: ['short', 'regular', 'long'],
        is_test: false
    },
    pagination: {
        per: PAGE_SIZES[0],
        page: 1
    }
};

const useSessionsStore = create<ISessionsState>()(
    persist(
        (set) => ({
            ...initialState,
            setVersion: (version) => {
                set({ version });
            },
            setHiddenColumns: (columns) => {
                set({ hiddenColumns: columns });
            },
            setDateRange: (dateRange) => {
                set({ dateRange });
            },
            setFilters: (filters) => {
                set((state) => {
                    return {
                        ...state,
                        filters: { ...state.filters, ...filters },
                        pagination: { ...state.pagination, page: 1 }
                    };
                });
            },
            replaceFilters: (filters) => {
                set((state) => {
                    return {
                        filters,
                        pagination: { ...state.pagination, page: 1 }
                    };
                });
            },
            resetFilters: () => {
                set((state) => {
                    return {
                        ...state,
                        dateRange: initialState.dateRange,
                        filters: initialState.filters,
                        pagination: { ...state.pagination, page: 1 }
                    };
                });
            },
            setPagination: (pagination) => {
                set((state) => ({
                    ...state,
                    pagination: { ...state.pagination, ...pagination }
                }));
            }
        }),
        {
            name: 'sessions-store',
            storage: createJSONStorage(() => globalThis.localStorage),
            // Select filters to save in localStorage
            partialize: (state) => {
                const { pagination, filters, ...rest } = state;

                const persistedFilters = omit(filters, [
                    'from_datetime',
                    'to_datetime'
                ]);

                return {
                    ...rest,
                    filters: persistedFilters,
                    pagination: {
                        per: pagination.per
                    }
                };
            },
            // Merge filters from localStorage with current state
            merge: (persistedState, currentState) => {
                return toMerged(currentState, persistedState as ISessionsState);
            },
            version: 1.35
        }
    )
);

export default useSessionsStore;
